import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { resolveThemeKey, type ThemeKey } from '@/themes';

export type Density = 'comfortable' | 'compact';

interface ThemeState {
  themeKey: ThemeKey;
  density: Density;
  setTheme: (key: ThemeKey) => void;
  setDensity: (d: Density) => void;
}

/**
 * Theme + density preferences are persisted to localStorage.
 *
 * The Phase-2 rewrite renamed every theme key (slateDark → obsidian, etc.).
 * The `migrate` step below rewrites old saved keys to their nearest new theme
 * via {@link resolveThemeKey} so a returning user lands on a sensible default
 * instead of a blank screen.
 */
export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      themeKey: 'cloudPro',
      density: 'comfortable',
      setTheme: (themeKey) => set({ themeKey }),
      setDensity: (density) => set({ density }),
    }),
    {
      name: 'school.theme',
      version: 2,
      migrate: (persisted) => {
        const p = (persisted ?? {}) as Partial<ThemeState> & { themeKey?: string };
        return {
          themeKey: resolveThemeKey(p.themeKey),
          density: p.density === 'compact' ? 'compact' : 'comfortable',
          setTheme: () => {},
          setDensity: () => {},
        } as ThemeState;
      },
    },
  ),
);
